import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Bell, ShoppingBag, CreditCard, Users, AlertCircle, Settings,
  CheckCheck, RotateCw, Trash2, Inbox, ChevronRight
} from 'lucide-react';
import Shell from '../dashboard/Shell';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import {
  fetchMerchantNotifications,
  markMerchantNotificationRead,
  markAllNotificationsRead,
  deleteNotification
} from './notificationsSlice';
import './MerchantNotifications.css';

// Date-grouping buckets for the feed. Linear/Slack-style — keeps the list
// scannable even when there are weeks of history.
const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const todayStart = () => startOfDay(new Date());
const yesterdayStart = () => {
  const d = todayStart();
  d.setDate(d.getDate() - 1);
  return d;
};
const weekStart = () => {
  const d = todayStart();
  d.setDate(d.getDate() - 7);
  return d;
};

const bucketFor = (createdAt) => {
  const t = new Date(createdAt).getTime();
  if (t >= todayStart().getTime()) return 'Today';
  if (t >= yesterdayStart().getTime()) return 'Yesterday';
  if (t >= weekStart().getTime()) return 'Earlier this week';
  return 'Older';
};

// Loose category derivation from the notification's type enum or text. Used
// for the icon, accent color, and filter chips. Backend enums like NEW_ORDER /
// PAYMENT_RECEIVED / KYC_PENDING are normalised here.
const categorize = (notification) => {
  const t = (notification.type || '').toString().toLowerCase();
  const text = `${notification.title || ''} ${notification.message || ''}`.toLowerCase();
  if (t.includes('order') || text.includes('order')) return 'order';
  if (t.includes('payment') || t.includes('settlement') || t.includes('wallet') || text.includes('payment') || text.includes('payout')) return 'payment';
  if (t.includes('customer') || text.includes('customer')) return 'customer';
  if (t.includes('warn') || t.includes('alert') || t.includes('error') || t.includes('fail')) return 'alert';
  return 'system';
};

const CATEGORY_META = {
  order:    { label: 'Orders',    icon: ShoppingBag,  tint: '#0d7463' },
  payment:  { label: 'Payments',  icon: CreditCard,   tint: '#2563eb' },
  customer: { label: 'Customers', icon: Users,        tint: '#7c3aed' },
  alert:    { label: 'Alerts',    icon: AlertCircle,  tint: '#dc2626' },
  system:   { label: 'System',    icon: Settings,     tint: '#52525b' },
};

const formatTime = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60)    return 'Just now';
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export default function MerchantNotifications() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useSecureAuth();
  const { items, status, error } = useSelector((s) => s.notifications);
  const [category, setCategory] = useState('all'); // all | order | payment | customer | alert | system
  const [onlyUnread, setOnlyUnread] = useState(false);

  useEffect(() => {
    if (isAuthenticated) dispatch(fetchMerchantNotifications());
  }, [isAuthenticated, dispatch]);

  const unreadCount = useMemo(() => items.filter((n) => !n.isRead).length, [items]);

  // Per-category counts for the filter chips. Computed once per items change.
  const categoryCounts = useMemo(() => {
    const counts = { all: items.length };
    items.forEach((n) => {
      const c = categorize(n);
      counts[c] = (counts[c] || 0) + 1;
    });
    return counts;
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((n) => {
      if (onlyUnread && n.isRead) return false;
      if (category !== 'all' && categorize(n) !== category) return false;
      return true;
    });
  }, [items, category, onlyUnread]);

  // Group filtered items into time buckets, preserving order within each.
  const grouped = useMemo(() => {
    const buckets = { Today: [], Yesterday: [], 'Earlier this week': [], Older: [] };
    filteredItems.forEach((n) => { buckets[bucketFor(n.createdAt)].push(n); });
    return Object.entries(buckets).filter(([, list]) => list.length > 0);
  }, [filteredItems]);

  // Tap = mark-read (if unread) + navigate to linked entity if `data.url` is set
  // by the backend. Falls back to no-op for purely informational notifications.
  const handleRowClick = (n) => {
    if (!n.isRead) dispatch(markMerchantNotificationRead(n.id));
    const url = n.data?.url || n.data?.link;
    if (url) navigate(url);
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    dispatch(deleteNotification(id));
  };

  const handleMarkAll = () => dispatch(markAllNotificationsRead());
  const handleRefresh = () => dispatch(fetchMerchantNotifications());

  const filterChips = ['all', 'order', 'payment', 'customer', 'alert', 'system']
    .filter((k) => k === 'all' || (categoryCounts[k] || 0) > 0);

  return (
    <Shell>
      <div className="notif-page">
        {/* ── Header ────────────────────────────────────────────── */}
        <header className="notif-header">
          <div className="notif-header-title">
            <h1>Notifications</h1>
            {unreadCount > 0 && <span className="notif-header-count">{unreadCount} new</span>}
          </div>
          <div className="notif-header-actions">
            <button
              type="button"
              className="notif-iconbtn"
              onClick={handleRefresh}
              aria-label="Refresh"
              disabled={status === 'loading'}
            >
              <RotateCw size={16} className={status === 'loading' ? 'is-spinning' : ''} />
            </button>
            <button
              type="button"
              className="notif-iconbtn notif-iconbtn-text"
              onClick={handleMarkAll}
              disabled={unreadCount === 0}
            >
              <CheckCheck size={16} />
              <span className="notif-iconbtn-label">Mark all read</span>
            </button>
          </div>
        </header>

        {/* ── Filter chips ─────────────────────────────────────── */}
        <div className="notif-filters" role="tablist">
          {filterChips.map((key) => {
            const meta = key === 'all' ? null : CATEGORY_META[key];
            const count = categoryCounts[key] || 0;
            const label = key === 'all' ? 'All' : meta.label;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={category === key}
                className={`notif-chip ${category === key ? 'is-active' : ''}`}
                onClick={() => setCategory(key)}
              >
                <span>{label}</span>
                <span className="notif-chip-count">{count}</span>
              </button>
            );
          })}
          <div className="notif-filters-spacer" />
          <label className="notif-unread-toggle">
            <input
              type="checkbox"
              checked={onlyUnread}
              onChange={(e) => setOnlyUnread(e.target.checked)}
            />
            <span>Unread only</span>
          </label>
        </div>

        {/* ── States ───────────────────────────────────────────── */}
        {status === 'loading' && items.length === 0 && (
          <div className="notif-skeleton">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="notif-skel-row">
                <div className="notif-skel-avatar" />
                <div className="notif-skel-body">
                  <div className="notif-skel-line notif-skel-line-1" />
                  <div className="notif-skel-line notif-skel-line-2" />
                </div>
              </div>
            ))}
          </div>
        )}

        {error && status === 'failed' && (
          <div className="notif-error">
            <AlertCircle size={18} />
            <span>{error || 'Failed to load notifications'}</span>
            <button type="button" className="notif-error-retry" onClick={handleRefresh}>Try again</button>
          </div>
        )}

        {status === 'succeeded' && filteredItems.length === 0 && (
          <div className="notif-empty">
            <div className="notif-empty-icon">
              <Inbox size={32} strokeWidth={1.5} />
            </div>
            <h3>{onlyUnread ? "You're all caught up" : 'No notifications yet'}</h3>
            <p>
              {onlyUnread
                ? 'No unread items in this view.'
                : "When something needs your attention — a new order, a payout, or a system update — it'll land here."}
            </p>
          </div>
        )}

        {/* ── Feed ─────────────────────────────────────────────── */}
        {status === 'succeeded' && filteredItems.length > 0 && (
          <div className="notif-feed">
            {grouped.map(([bucket, list]) => (
              <section key={bucket} className="notif-group">
                <h2 className="notif-group-title">{bucket}</h2>
                <ul className="notif-list">
                  {list.map((n) => {
                    const cat = categorize(n);
                    const meta = CATEGORY_META[cat];
                    const Icon = meta.icon;
                    const hasLink = !!(n.data?.url || n.data?.link);
                    return (
                      <li
                        key={n.id}
                        className={`notif-row ${n.isRead ? '' : 'is-unread'} ${hasLink ? 'is-clickable' : ''}`}
                      >
                        <button
                          type="button"
                          className="notif-row-main"
                          onClick={() => handleRowClick(n)}
                        >
                          <span
                            className="notif-row-avatar"
                            style={{ background: `${meta.tint}15`, color: meta.tint }}
                            aria-hidden="true"
                          >
                            <Icon size={18} />
                          </span>
                          <span className="notif-row-content">
                            <span className="notif-row-titleline">
                              <span className="notif-row-title">{n.title}</span>
                              {!n.isRead && <span className="notif-row-dot" aria-label="Unread" />}
                            </span>
                            <span className="notif-row-message">{n.message}</span>
                            <span className="notif-row-meta">
                              <span className="notif-row-cat">{meta.label}</span>
                              <span className="notif-row-sep">·</span>
                              <span className="notif-row-time">{formatTime(n.createdAt)}</span>
                            </span>
                          </span>
                          {hasLink && (
                            <span className="notif-row-chev" aria-hidden="true">
                              <ChevronRight size={16} />
                            </span>
                          )}
                        </button>
                        <button
                          type="button"
                          className="notif-row-delete"
                          aria-label="Delete notification"
                          onClick={(e) => handleDelete(e, n.id)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}
